<script setup>
/* Imports */
import {
  computed,
  inject,
} from 'vue';
import {
  promoteFeatureToTop, shouldShowCheckmark, filterFeatures,
} from '../helpers/lakeDetails.js';

/* Inject */
const showGarminMaps = inject('showGarminMaps');

/* Props */
const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  activeLake: {
    type: Object,
    default: () => ({}),
  },
  showLakeInfo: {
    type: Boolean,
    default: false,
  },
  translations: {
    type: Object,
    default: () => ({}),
  },
});

// shaded_relief and relief_shading should show the same translated value
const featureTranslations = {
  dynamic_lake_level: props.translations.ITFE_MARINE_MAPS_DYNAMIC_LAKE_LEVEL,
  dem: props.translations.ITFE_MARINE_MAPS_DEM,
  ngsr: !showGarminMaps.value
    ? props.translations.ITFE_MARINE_MAPS_NAVIONICS_NGSR_NAVIONICS
    : props.translations.ITFE_MARINE_MAPS_NGSR,
  shaded_relief: props.translations.ITFE_MARINE_MAPS_RELIEF_SHADING,
  relief_shading: props.translations.ITFE_MARINE_MAPS_RELIEF_SHADING,
  sidescan_mosaics: props.translations.ITFE_MARINE_MAPS_SIDESCANE_MOSIACS,
  navionics_3d: props.translations.ITFE_MARINE_MAPS_NAVIONICS_THREE_D,
};

const productTranslations = {
  NA: props.translations.ITFE_MARINE_MAPS_NAVIONICS_PLUS_LAKES_LABEL,
  NS: props.translations.ITFE_MARINE_MAPS_NAVIONICS_PLUS,
  NP: props.translations.ITFE_MARINE_MAPS_NAVIONICS_PLATINUM_PLUS_LAKES_LABEL,
  NV: props.translations.ITFE_MARINE_MAPS_NAVIONICS_VISION_PLUS,
  VP: props.translations.ITFE_MARINE_MAPS_NAVIONICS_VISION_PRO,
};

/* Computed */
const getShadedRelief = computed(() => props.activeLake?.features?.includes('shaded_relief'));
const getReliefShading = computed(() => props.activeLake?.features?.includes('relief_shading'));

/* Handle the features list (filter out and promotes important features to top) */
const features = computed(() => {
  const filters = [getShadedRelief.value, getReliefShading.value];

  const filteredFeatures = filterFeatures(props.activeLake?.features, filters);

  // Moves ngsr (Auto Guidance) to the first index of the array when => Garmin
  promoteFeatureToTop('ngsr', filteredFeatures);

  return filteredFeatures;
});

/* This computes products with their coresponding features */
const tableContent = computed(() => {
  const productKeys = Object.keys(props.activeLake?.included_in);

  // filtered products with no features
  const productKeysWithFeatures = productKeys.filter((productKey) => props.activeLake?.included_in[productKey]?.features?.length);
  const productsWithFeatures = {};

  productKeysWithFeatures.forEach((product) => {
    productsWithFeatures[product] = props.activeLake?.included_in[product]?.features;
  });

  return productsWithFeatures;
});

const products = computed(() => Object.keys(tableContent.value));
const shouldDisplayLakeDetails = computed(() => !props.isLoading && props.showLakeInfo && products.value.length);
</script>

<template>
  <div
    class="lake-details"
  >
    <g-copy class="lake-details__header">
      {{ props.translations.ITFE_MARINE_MAPS_INCLUDED_FEATURES }}
    </g-copy>
    <div
      v-if="shouldDisplayLakeDetails"
      class="lake-details__table"
    >
      <div class="lake-details__table__inner">
        <div class="lake-details__table__inner--row header">
          <g-copy
            v-for="product in products"
            :key="product"
            type="detail"
            text-align="center"
            class="lake-details__table__inner--cell"
          >
            {{ productTranslations[product] }}
          </g-copy>
        </div>
      </div>
      <template
        v-for="feature in features"
        :key="feature"
      >
        <div class="lake-details__table__inner">
          <div class="lake-details__table__inner--row body-header">
            <g-copy
              type="detail"
              text-align="left"
              class="lake-details__table__inner--cell"
            >
              {{ featureTranslations[feature] }}
            </g-copy>
          </div>
          <div class="lake-details__table__inner--row body">
            <div
              v-for="product in products"
              :key="product"
              class="lake-details__table__inner--cell"
            >
              <span
                v-if="shouldShowCheckmark(feature, product, tableContent)"
                class="checkmark"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
    <div
      v-else
      class="lake-details__info"
    >
      {{ props.translations.ITFE_MARINE_MAPS_NO_AVAILABLE_FEATURES }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lake-details {

  &__header {
    margin: 0.25rem 1rem;
    @include font-size(root);
    @include font-primary-weight-medium();
  }

  &__info {
    margin: 1rem;
    @include font-size(root);
    @include font-primary-weight-regular();
    font-style: italic;
    color: $color-gray-20;
  }

  &__table {
    display: flex;
    flex-direction: column;
    background: $color-white;
    margin: 0.75rem 1.25rem;

    &__inner {
      display: flex;
      flex-direction: column;

      &--row {
        display: flex;

        &.header {
          @include font-primary-weight-bold();
          text-transform: capitalize;
          margin-bottom: 1rem;

          > div {
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }

        &.body-header {
          font-size: 0.8rem;
          border-top: 1px solid $color-gray-90;
          @include font-primary-weight-bold();
          background-color: $color-gray-95;
          min-height: auto;
        }

        &.body {

          > div {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 35px;
          }
        }
      }

      :deep(.g__copy) {
        margin-bottom: 0;
      }

      &--cell {
        display: flex;
        justify-content: center;
        flex: 1;
        padding: 0.3rem 0.5rem;

        .checkmark {
          background: image-checkmark-black() center center no-repeat;
          height: 0.75rem;
          width: 0.75rem;
        }
      }
    }
  }
}
</style>
